import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { Chip, CircularProgress } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import BillAPI from 'api/orbitV1/BillAPI'
import OrgAPI from 'api/orbitV1/OrgAPI'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import { IFinanceBill } from 'types/api/v1/financeBill.type'
import { IOrgDetails } from 'types/api/v1/org.type'

import FilterMenu from './FilterMenu'
import generateHeaderColumns from './generateHeaderColumn'
import InvoiceDateRangePicker from './InvoiceDateRangePicker'

const FinanceTable = () => {
  const [startDate, setStartDate] = useState<Date>()
  const [endDate, setEndDate] = useState<Date>()
  const [companiesChecked, setCompaniesChecked] = useState<string[]>([])
  const [allFilters, setFilters] = useState<string[]>([])

  //to set default start date 3months before and end date to be start of this month
  useEffect(() => {
    const defaultStartDate = new Date()
    defaultStartDate.setDate(1)
    defaultStartDate.setMonth(new Date().getMonth() - 3)
    setStartDate(defaultStartDate)

    const defaultEndDate = new Date()
    defaultEndDate.setMonth(defaultEndDate.getMonth())
    defaultEndDate.setDate(1)
    setEndDate(defaultEndDate)
  }, [])

  //to add chips
  useEffect(() => {
    if (companiesChecked.length > 0 && !allFilters.includes('Companies')) {
      setFilters(['Companies', ...allFilters])
    }
  }, [companiesChecked])

  const handleDelete = (deletedFilter) => {
    const remainingChips = allFilters.filter(
      (restFilter) => restFilter !== deletedFilter
    )
    setFilters(remainingChips)

    switch (deletedFilter) {
      case 'Companies':
        setCompaniesChecked([])
        break

      default:
        break
    }
  }

  const {
    data: retrievedAllBillsData,
    error: retrievedAllBillsError,
    status: loadBillStatus
  } = useQuery(['getAllBillsWithinPeriod', startDate, endDate], async () => {
    const { data }: { data: IFinanceBill[] } = await BillAPI.getAllBills(
      startDate,
      endDate
    )
    return data
  })

  const {
    data: getAllOrgData,
    error: getOrgDataError,
    status: loadOrgDataStatus
  } = useQuery(['getOrgData'], async () => {
    const { data }: { data: IOrgDetails[] } = await OrgAPI.getAllOrgsDetails()
    return data
  })

  if (retrievedAllBillsError) {
    toast.error('Error retrieving bills. Please try again later!')
  }
  if (getOrgDataError) {
    toast.error('Error retrieving organisation data. Please try again later!')
  }

  //initial data load
  const consolidatedData = useMemo(() => {
    if (loadBillStatus === 'success' && loadOrgDataStatus === 'success') {
      const allData = retrievedAllBillsData.map((data) => {
        const companyDetails = getAllOrgData?.find(
          (org) => org._id === data.orgId
        )
        return { ...data, uen: companyDetails?.uen }
      })
      return allData
    }
  }, [getAllOrgData, retrievedAllBillsData])

  //to get all unique companies
  const uniqueCompanies = useMemo(() => {
    if (consolidatedData) {
      const allCompanies = consolidatedData?.map((company) => company.orgName)
      const uniqueCompaniesSet = new Set(allCompanies)
      return Array.from(uniqueCompaniesSet)
    }
  }, [getAllOrgData, retrievedAllBillsData])

  //to get all filtered companies after checking checkbox
  const filteredCompanies = useMemo(() => {
    if (consolidatedData) {
      const searchFilteredCompanies = consolidatedData.filter((data) =>
        companiesChecked.includes(data.orgName)
      )
      return searchFilteredCompanies
    }
  }, [companiesChecked])

  return consolidatedData ? (
    <>
      {uniqueCompanies && (
        <FilterMenu
          uniqueCompanies={uniqueCompanies}
          companiesChecked={companiesChecked}
          setCompaniesChecked={setCompaniesChecked}
        />
      )}
      {allFilters.map((searchFilter, index) => (
        <Chip
          key={`${searchFilter}-${index}`}
          label={searchFilter}
          onDelete={() => handleDelete(searchFilter)}
        />
      ))}
      <InvoiceDateRangePicker />
      <MUIDataGrid
        tableHeight='700px'
        sx={{ m: 2 }}
        columns={generateHeaderColumns()}
        rows={
          allFilters.length !== 0 && filteredCompanies
            ? filteredCompanies
            : consolidatedData
        }
        getRowId={(row: IFinanceBill) => row._id}
      />
    </>
  ) : (
    <CircularProgress />
  )
}

export default FinanceTable
